/**
 * The gateway's refusal of a signed-in person whose tenant is not active
 * (user_auth_HLD.md R-MT-08; `internal/api/middleware/tenant_status.go`).
 *
 * The gateway answers every authenticated request, and the sign-in and refresh
 * proxy, with one of two codes in the error envelope's `error` field:
 * `VERISA.AUTH.TENANT_SUSPENDED` (403) or `VERISA.AUTH.TENANT_GONE` (410).
 * Neither is retryable by the client: the session is over until a platform
 * administrator reactivates the tenant. The API client turns either into
 * {@link TENANT_REFUSED_EVENT}; AuthContext signs the person out and the login
 * page says why.
 */

export const TENANT_SUSPENDED_CODE = "VERISA.AUTH.TENANT_SUSPENDED";
export const TENANT_GONE_CODE = "VERISA.AUTH.TENANT_GONE";

/** The stable, locale-agnostic reason the login page maps to a sentence. */
export type TenantRefusal = "auth/tenant-suspended" | "auth/tenant-gone";

/** Dispatched on `window` with a {@link TenantRefusedEventDetail}. */
export const TENANT_REFUSED_EVENT = "auth:tenant-refused";

export interface TenantRefusedEventDetail {
  refusal: TenantRefusal;
}

/** The refusal an error body carries, or null for any other body. */
export function tenantRefusalFromBody(body: unknown): TenantRefusal | null {
  if (typeof body !== "object" || body === null) return null;
  const code = (body as { error?: unknown }).error;
  if (code === TENANT_SUSPENDED_CODE) return "auth/tenant-suspended";
  if (code === TENANT_GONE_CODE) return "auth/tenant-gone";
  return null;
}

export function dispatchTenantRefused(refusal: TenantRefusal): void {
  const detail: TenantRefusedEventDetail = { refusal };
  window.dispatchEvent(new CustomEvent(TENANT_REFUSED_EVENT, { detail }));
}
